<template>
    <v-container fluid class="fill-height hr-login">
        <v-row align="center" justify="center" class="fill-height">
            <v-col cols="12" sm="8" md="6" lg="4">
                <v-card elevation="8" rounded="lg" class="hr-card">
                    <v-card-title class="text-center pa-6">
                        <div class="d-flex flex-column align-center">
                            <v-img src="/logo.png" max-width="80" class="mb-4" alt="Logo Recruitment App"></v-img>
                            <h1 class="text-h4 mb-2">Espace Employé</h1>
                            <p class="text-subtitle-1 text-grey-darken-1">
                                Accès réservé aux employés, RH et administrateurs
                            </p>
                        </div>
                    </v-card-title>

                    <v-card-text class="pa-6">
                        <v-form @submit.prevent="handleLogin" ref="loginFormRef">
                            <v-text-field v-model="loginForm.email" label="Email professionnel" type="email"
                                prepend-inner-icon="mdi-email" variant="outlined" :rules="[rules.required, rules.email]"
                                required class="mb-4"></v-text-field>

                            <v-text-field v-model="loginForm.password" label="Mot de passe"
                                :type="showPassword ? 'text' : 'password'" prepend-inner-icon="mdi-lock"
                                :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                @click:append-inner="showPassword = !showPassword" variant="outlined"
                                :rules="[rules.required]" required class="mb-4"></v-text-field>

                            <v-checkbox v-model="loginForm.rememberMe" label="Se souvenir de moi"
                                class="mb-4"></v-checkbox>

                            <v-btn type="submit" color="primary" size="large" block :loading="userStore.isLoading"
                                class="mb-4">
                                <v-icon start>mdi-login</v-icon>
                                Se connecter
                            </v-btn>

                            <div class="text-center">
                                <v-btn variant="text" color="primary" @click="showResetPassword = true">
                                    Mot de passe oublié ?
                                </v-btn>
                            </div>
                        </v-form>

                        <v-divider class="my-6"></v-divider>

                        <!-- Lien vers l'espace candidat -->
                        <div class="text-center">
                            <p class="text-caption text-grey mb-2">Vous cherchez un emploi ?</p>
                            <v-btn variant="text" color="success" size="small" @click="goToCandidateSpace">
                                Accéder à l'espace candidat
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Dialog Reset Password -->
        <v-dialog v-model="showResetPassword" max-width="400">
            <v-card>
                <v-card-title>
                    <v-icon class="mr-2">mdi-lock-reset</v-icon>
                    Réinitialiser le mot de passe
                </v-card-title>

                <v-card-text>
                    <v-form @submit.prevent="handleResetPassword" ref="resetForm">
                        <v-text-field v-model="resetEmail" label="Email professionnel" type="email" variant="outlined"
                            prepend-inner-icon="mdi-email" :rules="[rules.required, rules.email]"
                            required></v-text-field>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="showResetPassword = false">Annuler</v-btn>
                    <v-btn color="primary" @click="handleResetPassword" :loading="isResettingPassword">
                        Envoyer
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup lang="ts">
import { apiService } from '@/services/api'
import { useSnackbarStore } from '@/stores/snackbar'
import { useUserStore } from '@/stores/user'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const userStore = useUserStore()
const snackbarStore = useSnackbarStore()

const showPassword = ref(false)
const showResetPassword = ref(false)
const isResettingPassword = ref(false)

const loginForm = ref({
    email: '',
    password: '',
    rememberMe: false
})

const resetEmail = ref('')

// Fonction pour rediriger vers l'espace candidat
const goToCandidateSpace = () => {
    window.location.href = '/?mode=candidate'
}

const rules = {
    required: (value: string) => !!value || 'Ce champ est requis',
    email: (value: string) => /.+@.+\..+/.test(value) || 'Email invalide'
}

const handleLogin = async () => {
    // Validation simple
    if (!loginForm.value.email || !loginForm.value.password) {
        snackbarStore.warning('Veuillez remplir tous les champs')
        return
    }

    try {
        const result = await userStore.login({
            email: loginForm.value.email,
            password: loginForm.value.password,
            rememberMe: loginForm.value.rememberMe
        })

        if (result.success) {
            snackbarStore.success('Connexion réussie')

            // Redirection selon le rôle (uniquement pour les rôles internes)
            if (userStore.hasRole('admin')) {
                router.push('/admin/dashboard')
            } else if (userStore.hasRole('hr')) {
                router.push('/hr/dashboard')
            } else {
                // Si ce n'est ni admin ni HR, c'est probablement un candidat qui s'est trompé de page
                snackbarStore.warning('Compte candidat détecté. Redirection vers l\'espace candidat...')
                goToCandidateSpace()
            }
        }
    } catch (error: any) {
        snackbarStore.error(error.message || 'Erreur de connexion')
    }
}

const handleResetPassword = async () => {
    if (!resetEmail.value) {
        snackbarStore.warning('Veuillez saisir votre email')
        return
    }

    try {
        isResettingPassword.value = true
        await apiService.auth.resetPassword(resetEmail.value)
        snackbarStore.success('Instructions envoyées par email')
        showResetPassword.value = false
        resetEmail.value = ''
    } catch (error: any) {
        console.error('Erreur lors de l\'envoi du lien de réinitialisation:', error)
        snackbarStore.error('Erreur lors de l\'envoi du lien de réinitialisation')
    } finally {
        isResettingPassword.value = false
    }
}

onMounted(() => {
    // Si l'utilisateur est déjà connecté, rediriger selon son rôle
    if (userStore.isAuthenticated) {
        if (userStore.hasRole('admin')) {
            router.push('/admin/dashboard')
        } else if (userStore.hasRole('hr')) {
            router.push('/hr/dashboard')
        } else if (userStore.hasRole('candidate')) {
            // Les candidats ne devraient pas être ici
            goToCandidateSpace()
        }
    }
})
</script>

<style scoped>
.hr-login {
    background: linear-gradient(135deg, #1976D2 0%, #0D47A1 100%);
    min-height: 100vh;
}

.hr-card {
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.95);
}

.v-text-field {
    margin-bottom: 1rem;
}
</style>
